
import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, H1, H2, Panel, Text, ProgressBar, ProgressCircle, Message, Link, Flex, FlexItem, Form, FormGroup, Input, Select, AlertsManager, Grid, GridItem } from '@bigcommerce/big-design';
import { SettingsIcon } from '@bigcommerce/big-design-icons';
import { BrowserRouter, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAuth, login, installScript, uninstallScript } from './authSlice';
import { fetchReviews } from './reviewsSlice';
import { loadSettings, loadDefaultSettings } from './settingsSlice';
import { useFormik } from 'formik'
import { alertsManager } from './alerts';

// Import các component đã tách
import StarRating from './components/StarRating';
import Reviews from './components/Reviews';
import ReviewImages from './components/ReviewImages';
import ReviewEdit from './components/ReviewEdit';
import Settings from './components/Settings';
import { Order, Subscription } from './components/Order';

// Re-exported for existing imports (`import { alertsManager } from '../App'`).
export { alertsManager };

const useRouteTo = () => {
    const history = useHistory();
    const baseParams = new URLSearchParams(useLocation().search);

    return (params) => {
        const urlParams = new URLSearchParams(params);
        urlParams.set('signed_payload', baseParams.get('signed_payload'));
        history.push(`?${urlParams.toString()}`);
    }
};

const RoutedApp = () => {
    const dispatch = useDispatch();
    const routeTo = useRouteTo();
    const loaded = useSelector(state => state.auth.loaded);
    const loading = useSelector(state => state.auth.loading);
    const scriptProcessing = useSelector(state => state.auth.scriptProcessing);
    const reviews = useSelector(state => state.reviews);
    const query = new URLSearchParams(useLocation().search);
    const route = query.get('route');
    const reviewId = Number(query.get('reviewId'));
    const page = Number(query.get('page')) || 1;
    const limit = Number(query.get('limit')) || 0;
    const signedPayload = query.get('signed_payload');
    const [showAppInstalledMsg, setShowAppInstalledMsg] = useState(query.get('installed'));
    const review = reviewId && reviews && loaded ? reviews.data.filter(item => item.reviewId === reviewId)[0] : null;
    const loginSignedPayload = useSelector(state => state.auth.loginSignedPayload);
    const history = useHistory();
    const { trial, expire = null, site_id = 0, sites = [], scriptInstalled, settings, defaultSettings } = useSelector(state => state.auth.data) || {};
    const expiredDate = new Date(expire).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric'  });
    const isExpired = expire && new Date(expire) < new Date(Date.now());

    query.delete('route');
    query.delete('reviewId');

    const appUrl = `?${query.toString()}`;

    const loginForm = useFormik({
        initialValues: {
            email: '',
            access_token: '',
            store_hash: '',
        },
        onSubmit: values => {
            dispatch(login(values));
        }
    });

    useEffect(() => {
        if (signedPayload && !loaded) {
            dispatch(fetchAuth());
        }
    }, [loaded, signedPayload]);

    useEffect(() => {
        if (signedPayload) {
            dispatch(fetchReviews());
        }
    }, [page, limit, signedPayload]);

    useEffect(() => {
        if (!signedPayload && loginSignedPayload) {
            history.push(`${appUrl}&signed_payload=${loginSignedPayload}`);
        }
    }, [signedPayload, loginSignedPayload]);

    const [siteIdValue, setSiteIdValue] = useState();
    const siteOptions = sites.map(site => ({ value: site.id, content: site.channel_name, description: site.url }));

    const changeSite = id => {
        setSiteIdValue(id);
        if (id !== site_id) dispatch(fetchAuth(id));
    };

    useEffect(() => {
        setSiteIdValue(site_id);
    }, [site_id]);

    useEffect(() => {
        dispatch(loadSettings(settings));
    }, [site_id, settings]);

    useEffect(() => {
        dispatch(loadDefaultSettings(defaultSettings));
    }, [defaultSettings]);

    return (
        <>
            <Flex alignItems="center" justifyContent="space-between" marginBottom="large">
                <FlexItem>
                    <H1 margin="none">Product Review Images</H1>
                </FlexItem>
                <FlexItem style={{textAlign: 'right'}}>
                    <Link href="https://papathemes.com/product-review-images-guide/" target="_blank" external marginRight="medium">Documentation</Link>
                    <Link href="https://papathemes.com/contact-us/" target="_blank" external>Support</Link>
                    {loaded && !isExpired
                        ? <Box marginTop="small"><Subscription /></Box>
                        : ''}
                </FlexItem>
            </Flex>

            {!signedPayload ?
                <Panel
                    header="Your Store API Login">
                    <Form onSubmit={loginForm.handleSubmit}>
                        <FormGroup>
                            <Input
                                name="email"
                                label="Email"
                                type="email"
                                description="Please enter your store owner's email"
                                placeholder="Email address"
                                value={loginForm.values.email}
                                onChange={loginForm.handleChange}
                                error={loginForm.touched.email && loginForm.errors.email}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                name="access_token"
                                label="Access Token"
                                type="text"
                                description="Please enter your V2/V3 API Access token with OAuth Scopes: Products = Modify."
                                placeholder="Access token"
                                value={loginForm.values.access_token}
                                onChange={loginForm.handleChange}
                                error={loginForm.touched.access_token && loginForm.errors.access_token}
                                required />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                name="store_hash"
                                label="Store Hash"
                                type="text"
                                description="Please enter your store hash. Example: vsupzbml60"
                                placeholder="Store hash"
                                value={loginForm.values.store_hash}
                                onChange={loginForm.handleChange}
                                error={loginForm.touched.store_hash && loginForm.errors.store_hash}
                                required />
                        </FormGroup>
                        <Box marginTop="xxLarge">
                            <Button disabled={loading !== 'idle'}>Sign In</Button>
                        </Box>
                    </Form>
                </Panel>
            : (loaded ? (
                <>
                    {showAppInstalledMsg ?
                        <Message
                            header="Success"
                            messages={[{ text: <><strong>Product Review Images app</strong> installed successfully!</> }]}
                            onClose={() => setShowAppInstalledMsg(false)}
                            marginVertical="medium"
                        /> : ''}

                    {isExpired ?
                        <Flex flexDirection="column" alignItems="center">
                            {trial
                                ? <FlexItem marginBottom="medium"><Text color="danger40">Your trial has been expired on {expiredDate}. Please subscribe now to continue using this app</Text></FlexItem>
                                : <FlexItem marginBottom="medium"><Text color="danger40">Your subscription has been expired on {expiredDate}. Please review now to continue using this app</Text></FlexItem>}
                            <FlexItem><Order /></FlexItem>
                        </Flex>
                    : <>
                        {!route ? (
                            loading === 'pending' ? (
                                <Box marginVertical="large">
                                    <ProgressBar />
                                </Box>
                            ) : (<>
                                <Grid gridColumns="auto max-content" style={{alignItems: 'end'}}>
                                    <GridItem>
                                        <Box marginBottom="medium" style={{maxWidth: '300px'}}>
                                            <Select
                                                label="Channel Sites"
                                                onOptionChange={changeSite}
                                                options={siteOptions}
                                                placeholder={'Choose site to apply'}
                                                required
                                                value={siteIdValue}
                                                filterable={false}
                                            />
                                        </Box>
                                    </GridItem>
                                    <GridItem>
                                        <Box marginBottom={'medium'}>
                                            <Button variant="secondary" onClick={() => routeTo({ route: 'settings' })} iconLeft={<SettingsIcon />}>Settings</Button>
                                            {scriptInstalled
                                                ? <Button actionType="destructive" variant="secondary" onClick={() => dispatch(uninstallScript())} isLoading={scriptProcessing === 'pending'}>Remove the script from this site</Button>
                                                : <Button actionType="normal" onClick={() => dispatch(installScript())} isLoading={scriptProcessing === 'pending'}>Install the script on this site</Button>}
                                        </Box>
                                    </GridItem>
                                </Grid>
                                <Reviews reviews={reviews} baseUrl={appUrl} />
                            </>)
                        ) : ''}
                        {route === 'manageimages' && review ? <ReviewImages review={review} backUrl={appUrl} /> : ''}
                        {route === 'edit' ? <ReviewEdit review={review} backUrl={appUrl} /> : ''}
                        {route === 'settings' && <Settings />}
                    </>}

                </>
            ) : (
                <Box marginVertical="large">
                    <ProgressBar />
                </Box>
            ))}

            <Text color="secondary50" marginVertical="xxLarge">©️ Product Review Images developed by <Link href="https://papathemes.com/" target="_blank">PapaThemes</Link>.</Text>
        </>
    );
};

export const App = () =>
    <BrowserRouter>
        <AlertsManager manager={alertsManager} />
        <RoutedApp />
    </BrowserRouter>
